"use client";

import { Microscope, Flame, Cog, Activity, Crosshair, Wind, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CoreValuesMission() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const certifications = ["ISO 9001 CERTIFIED", "CE MARK COMPLIANT", "EUROPEAN STANDARDS", "INDUSTRIAL GRADE"];
  const features = [{
    id: "S01",
    icon: Microscope,
    title: "QUALITY CONTROL",
    description: "Rigorous inspection of every steel coil and panel batch to ensure structural integrity and finish consistency."
  }, {
    id: "S02",
    icon: Cog,
    title: "PRECISION CUTTING",
    description: "Advanced machinery for exact trapezoidal sheet dimensions, minimizing waste and ensuring perfect installation."
  }, {
    id: "S03",
    icon: Flame,
    title: "THERMAL EFFICIENCY",
    description: "High-performance insulation cores designed to meet strict European energy efficiency regulations."
  }, {
    id: "S04",
    icon: Activity,
    title: "STRESS TESTING",
    description: "Load-bearing analysis for all roofing and wall systems to guarantee safety under extreme weather conditions."
  }, {
    id: "S05",
    icon: Crosshair,
    title: "CUSTOM FABRICATION",
    description: "Tailored flashing and accessory production to match specific architectural requirements and RAL colors."
  }, {
    id: "S06",
    icon: Wind,
    title: "WEATHER RESISTANCE",
    description: "Advanced coating technologies providing superior protection against corrosion, UV radiation, and moisture."
  }];
  return <section data-section-id="3990" id="core-values-mission" ref={sectionRef} className="bg-background text-foreground relative overflow-hidden py-20 md:py-28 px-4 md:px-8">
      {}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: "linear-gradient(to right, var(--foreground) 1px, transparent 1px), linear-gradient(to bottom, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "40px 40px"
    }} aria-hidden="true" />

      {}
      <div className="absolute top-8 left-4 right-4 md:left-8 md:right-8 flex items-center pointer-events-none" aria-hidden="true">
        <span className="text-primary font-mono text-lg leading-none">+</span>
        <div className="flex-1 border-t border-dashed border-border mx-2" />
        <span className="text-primary font-mono text-lg leading-none">+</span>
      </div>

      <div className="relative max-w-7xl mx-auto pt-8">
        {}
        <div className={`mb-12 md:mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="font-mono text-xs md:text-sm text-primary tracking-widest uppercase">
            // 02 — OUR CORE VALUES
          </span>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div className={`transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                <h2 className="font-default font-medium text-5xl sm:text-6xl md:text-7xl lg:text-6xl xl:text-7xl uppercase leading-[0.95] tracking-tight text-foreground">
                  Swiss quality.
                  <br />
                  <span className="text-primary">Balkan</span>
                  <br />
                  reliability.
                </h2>

                <p className="mt-8 text-base md:text-lg text-muted-foreground leading-relaxed max-w-md">
                  At Swiss Solutions, we bridge the gap between premium European engineering and local construction needs. Our materials are sourced from certified manufacturers, ensuring every project meets the highest standards of durability and performance.
                </p>

                {}
                <div className="mt-10 space-y-3">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-px w-8 bg-primary" />
                    <span className="font-mono text-xs text-muted-foreground uppercase tracking-wider">
                      Standards & Certifications
                    </span>
                  </div>
                  <div className="flex flex-col gap-2">
                    {certifications.map((cert, i) => <Badge key={cert} data-index={i} variant="outline" className="font-mono text-xs border-border text-foreground bg-card/40 px-3 py-1.5 w-fit tracking-wider">
                        {cert}
                      </Badge>)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {features.map((feature, i) => {
              const Icon = feature.icon;
              return <div key={feature.id} data-index={i} className={`group relative bg-card border border-border hover:border-primary transition-all duration-500 p-6 md:p-7 overflow-hidden ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
                transitionDelay: isVisible ? `${200 + i * 80}ms` : "0ms"
              }}>
                    {}
                    <div className="absolute top-4 left-5 font-mono text-xs text-muted-foreground tracking-wider">
                      {feature.id}
                    </div>

                    {}
                    <div className="absolute top-3 right-3 w-3 h-3 border-t border-r border-border group-hover:border-primary transition-colors duration-500" aria-hidden="true" />

                    {}
                    <div className="mt-8 mb-5">
                      <Icon className="w-9 h-9 text-primary group-hover:scale-110 transition-transform duration-500 stroke-[1.25]" aria-hidden="true" />
                    </div>

                    {}
                    <h3 className="font-default font-medium text-base md:text-lg uppercase tracking-wider text-foreground mb-2">
                      {feature.title}
                    </h3>

                    {}
                    <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                      {feature.description}
                    </p>

                    {}
                    <div className="relative pt-4 mt-auto">
                      <div className="absolute top-0 left-0 right-0 h-px border-t border-dashed border-border group-hover:border-primary transition-colors duration-500" />
                      <Link to="/ProductsPage" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground group-hover:text-primary transition-all duration-500 opacity-70 group-hover:opacity-100">
                        <span>View Details</span>
                        <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-500" />
                      </Link>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-8 left-4 right-4 md:left-8 md:right-8 flex items-center pointer-events-none" aria-hidden="true">
        <span className="text-primary font-mono text-lg leading-none">+</span>
        <div className="flex-1 border-t border-dashed border-border mx-2" />
        <span className="text-primary font-mono text-lg leading-none">+</span>
      </div>
    </section>;
}